import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { CacheService, PlatformBrowserService, PlatformInfoService } from '@tgx/shared/services';
import { startWith, switchMap, take } from 'rxjs';
import { OnboardingStatus, ProfileCache, SelectorOrganization } from '@tgx/shared/interfaces';

/**
 * Guards onboarding-implementation routes.
 * - Redirects to dashboard when no onboarding is pending.
 * - Enforces the correct onboarding path (buyer root vs seller push/pull).
 */
export const requirePendingOnboardingGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const cacheService = inject(CacheService);
  const platformInfoService = inject(PlatformInfoService);
  const platformBrowserService = inject(PlatformBrowserService);

  if (!platformInfoService.isBrowser()) {
    return true;
  }

  return platformInfoService.organizationSelected$.pipe(
    startWith(platformInfoService.selectedOrg ?? null),
    take(1),
    switchMap(async (org: SelectorOrganization | null): Promise<boolean | UrlTree> => {
      // During organization switch, storage is updated before route guard execution.
      // Prioritize persisted org code to avoid evaluating with a stale in-memory emission.
      const orgCode = platformBrowserService.getLocalStorageItem('organizationCode') || org?.code;

      // If organization context is still unavailable during bootstrap, do not block the app.
      if (!orgCode) {
        return true;
      }

      const profile: ProfileCache = (await cacheService.retrieveProfilesByCodes([orgCode]))[0];
      if (!profile) {
        return true;
      }

      const isSellerOnboardingRoute = state.url.startsWith('/onboarding-implementation/seller-');
      const buyerStatus = profile?.buyerStatus ?? OnboardingStatus.None;
      const sellerStatus = profile?.sellerStatus ?? OnboardingStatus.None;
      const sellerTargetUrl = profile.isSellerPush
        ? '/onboarding-implementation/seller-push'
        : '/onboarding-implementation/seller-pull';
      const buyerTargetUrl = '/onboarding-implementation';

      // No pending onboarding: onboarding routes must not be accessible.
      if (buyerStatus !== OnboardingStatus.Pending && sellerStatus !== OnboardingStatus.Pending) {
        return router.createUrlTree(['/dashboard']);
      }

      // Only seller pending: force seller onboarding route.
      if (sellerStatus === OnboardingStatus.Pending && buyerStatus !== OnboardingStatus.Pending) {
        if (!isSellerOnboardingRoute) {
          return router.createUrlTree([sellerTargetUrl]);
        }
        return true;
      }

      // Only buyer pending: force buyer onboarding route.
      if (buyerStatus === OnboardingStatus.Pending && sellerStatus !== OnboardingStatus.Pending) {
        if (isSellerOnboardingRoute) {
          return router.createUrlTree([buyerTargetUrl]);
        }
        return true;
      }

      // Both pending: buyer onboarding root is the actionable entrypoint.
      if (
        buyerStatus === OnboardingStatus.Pending &&
        sellerStatus === OnboardingStatus.Pending &&
        isSellerOnboardingRoute
      ) {
        return router.createUrlTree([buyerTargetUrl]);
      }

      return true;
    }),
  );
};
